import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatUsualWeeklyUnits } from '../../../utils/alertBadgeHelpers';

const SlotTooltip = ({ slot, period = 30, unitsPerDay }) => {
  const { t } = useTranslation();
  if (!slot || !slot.name) return null;

  const stockPct = slot.total_stock_on_fillup > 0
    ? Math.round((slot.current_stock / slot.total_stock_on_fillup) * 100)
    : null;

  const rows = [];

  // Open technical alert: product has not sold for N weeks
  if (slot.alert) {
    rows.push({
      label: t('machines_tooltipNoSales'),
      value: t('machines_alertWeeksNoSales', { weeks: slot.alert.weeksSince }),
      context: slot.alert.estimatedLossWeeklyUnits > 0
        ? `(${t('alertUsualWeeklySales', { units: formatUsualWeeklyUnits(slot.alert.estimatedLossWeeklyUnits) })})`
        : null
    });
  }

  // Average daily sales (absolute primary value; 0 is shown as 0, not hidden).
  // On the sales heatmap it is the lane's own value, the same one the card and its color show.
  if (unitsPerDay != null) {
    rows.push({
      label: t('machines_tooltipSalesLabel'),
      value: `${unitsPerDay.toFixed(1)} ${t('machines_tooltipUnitsPerDay')}`,
      context: null
    });
  } else if (slot.productAvgSales != null) {
    const prevAvg = slot.previousPeriodSales > 0 ? (slot.previousPeriodSales / period).toFixed(1) : null;
    rows.push({
      label: t('machines_tooltipSalesLabel'),
      value: `${Number(slot.productAvgSales).toFixed(1)} ${t('machines_tooltipUnitsPerDay')}`,
      context: prevAvg ? `(${t('machines_was')} ${prevAvg}u/d)` : null
    });
  }

  // Daily revenue per slot
  if (slot.productDailyRevenue != null && slot.productDailyRevenue > 0) {
    rows.push({
      label: t('machines_tooltipRevenueLabel', 'Revenue'),
      value: `€${Number(slot.productDailyRevenue).toFixed(2)}/d`,
      context: null
    });
  }

  // Margin per unit (absolute)
  if (slot.productMargin != null && slot.productMargin !== 0) {
    const prevMargin = slot.previousPeriodMargin > 0 ? `(${t('machines_was')} €${Number(slot.previousPeriodMargin).toFixed(2)}/u)` : null;
    rows.push({
      label: t('machines_tooltipMarginLabel'),
      value: `€${Number(slot.productMargin).toFixed(2)} / ${t('machines_chipUnit')}`,
      context: prevMargin
    });
  }

  // Stock (already absolute)
  if (stockPct !== null) {
    rows.push({
      label: 'Stock',
      value: `${slot.current_stock}/${slot.total_stock_on_fillup} (${stockPct}%)`,
      context: null
    });
  }

  // Category
  if (slot.productCategory) {
    rows.push({
      label: t('machines_viewCategory'),
      value: slot.productCategory,
      context: null
    });
  }

  return (
    <Box sx={{ p: 0.5, minWidth: 180 }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#fff', mb: 0.25 }}>
        {slot.name}
      </Typography>
      {slot.selection_id && (
        <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mb: 0.75 }}>
          {t('machines_tooltipSlot')} {slot.selection_id}
        </Typography>
      )}

      {rows.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rows.map((row, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                {row.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '11px', color: '#fff', fontWeight: 500 }}>
                  {row.value}
                </Typography>
                {row.context && (
                  <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)' }}>
                    {row.context}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {slot.experiment && (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          {(slot.experiments?.length > 1 ? slot.experiments : [slot.experiment]).map((exp, idx) => (
            <Box key={exp.id || idx} sx={{ mb: idx < (slot.experiments?.length || 1) - 1 ? 0.5 : 0 }}>
              <Typography sx={{ fontSize: '11px', color: '#93c5fd', fontWeight: 500 }}>
                {t('machines_tooltipExperiment')}: {t(exp.status)} #{exp.id}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
                {exp.experimentType === 'promo_precio_unitario'
                  ? `Precio -${exp.candidateDiscounts?.[0] || exp.discount || 15}%`
                  : exp.experimentType === 'alza_precio_sugerido'
                  ? `Precio +${exp.markup || 0}%`
                  : exp.experimentType === 'reemplazo_producto'
                  ? `${(exp.originalProduct?.name || '').slice(0, 15)} → ${(exp.candidateNames?.[0] || '').slice(0, 15)}`
                  : exp.experimentType === 'ampliar_carril'
                  ? `➕ ${(exp.originalProduct?.name || '').slice(0, 15)} → ${(exp.candidateNames?.[0] || '').slice(0, 15)}`
                  : `Combo → ${(exp.candidateNames?.[0] || '').slice(0, 20)}`}
              </Typography>
              {exp.dateFrom && (
                <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>
                  {new Date(exp.dateFrom).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                  {' → '}
                  {new Date(exp.dateTo).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SlotTooltip;
